/**
 * @file routes/_app/bulletin.index.tsx
 * @description 公佈欄頁面 — 列表式公告板。
 *
 * 資料來源：GET /api/bulletin（共用公告池，所有已登入角色都讀同一份）。
 * 每筆公告附帶當前用戶的 `isRead` / `readAt`（後端 LEFT JOIN bulletin_reads）。
 *
 * 與舊版的差異：
 * - 不再讀 /api/notifications（那是「個人收件匣」）。
 * - 已讀狀態透過 POST /api/bulletin/:id/read 寫入 bulletin_reads 表，不複製公告本體。
 * - 「新增公告」入口僅對具備 bulletin.publish 能力的使用者顯示，並改調 POST /api/bulletin。
 * - 2026-09-13 回報序15：公告列表不再提供「刪除」（列表行與詳情彈窗皆移除），
 *   刪除統一在「公告管理」（/bulletin/admin）操作，避免兩處重複入口。
 *
 * 功能：搜尋、類型篩選（依 isPinned 分類）、檢視、標記已讀、（限具發布權限者）新增。
 */

import { createFileRoute } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Pagination, Select, Spin, Switch, Tooltip } from 'antd';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  Clock,
  Eye,
  Info,
  Megaphone,
  Pin,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-tw';
import { userCan } from '@zhcb/shared';
import { useAuthStore } from '@/lib/auth-context';
import { apiClient } from '@/lib/api-client';
import { EmptyState, FilterBar, PageShell, SectionCard, StatusPill } from '@/components/shell';
import { color, font, leading, radius, space, text, tracking, weight } from '@/styles/tokens';

dayjs.extend(relativeTime);
dayjs.locale('zh-tw');

const { TextArea } = Input;

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/bulletin/')({ component: BulletinPage });

/* ════════════════════════════════════════════════
 * 類型 / 常量
 * ════════════════════════════════════════════════ */

/**
 * 後端 GET /api/bulletin 回傳的公告 row（單筆）。
 * 對應 `bulletins` 表 + LEFT JOIN bulletin_reads 衍生的 isRead/readAt。
 */
interface Bulletin {
  id: number;
  title: string;
  body: string;
  audience: string;
  isPinned: boolean;
  publishedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: number | null;
  isRead: boolean;
  readAt: string | null;
}

/** 後端列表回應 */
interface BulletinListResponse {
  items: Bulletin[];
}

/** 客戶端篩選條件 */
type FilterKind = 'all' | 'pinned' | 'unread';

const PAGE_SIZE = 10;

const FILTER_OPTIONS: { value: FilterKind; label: string }[] = [
  { value: 'all', label: '全部公告' },
  { value: 'pinned', label: '僅置頂' },
  { value: 'unread', label: '僅未讀' },
];

/** 公告分類 → 顯示樣式 */
function getDisplayConfig(b: Bulletin) {
  if (b.isPinned) {
    return {
      label: '重要公告',
      tone: 'warning' as const,
      icon: <AlertTriangle size={14} />,
      accent: color.state.warning,
    };
  }
  return {
    label: '一般公告',
    tone: 'info' as const,
    icon: <Info size={14} />,
    accent: color.state.info,
  };
}

/* ════════════════════════════════════════════════
 * 子組件：單則公告 row
 * ════════════════════════════════════════════════ */

interface BulletinRowProps {
  item: Bulletin;
  onView: (item: Bulletin) => void;
  onMarkRead: (id: number) => void;
}

function BulletinRow({ item, onView, onMarkRead }: BulletinRowProps) {
  const config = getDisplayConfig(item);
  const isUnread = !item.isRead;

  /** 相對時間格式化（>7 天用絕對日期） */
  const formatTime = (dateStr: string) => {
    const date = dayjs(dateStr);
    const now = dayjs();
    if (now.diff(date, 'day') > 7) return date.format('YYYY-MM-DD HH:mm');
    return date.fromNow();
  };

  /** 公告 body 是富文本 HTML，列表只顯示純文本摘要 */
  const summary = useMemo(
    () =>
      item.body
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim(),
    [item.body],
  );

  return (
    <div
      style={{
        display: 'flex',
        gap: space[4],
        padding: `${space[4]}px ${space[5]}px`,
        borderBottom: `1px solid ${color.border.subtle}`,
        background: isUnread ? color.surface.muted : 'transparent',
        position: 'relative',
        transition: `background 120ms cubic-bezier(0.16, 1, 0.3, 1)`,
      }}
    >
      {/* 左側類型色條（未讀時顯示） */}
      {isUnread && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: config.accent,
          }}
        />
      )}

      {/* 內容區 */}
      <div style={{ minWidth: 0, flex: 1 }}>
        {/* meta 行 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
            marginBottom: space[2],
            flexWrap: 'wrap',
          }}
        >
          <StatusPill tone={config.tone} icon={config.icon} size="sm">
            {config.label}
          </StatusPill>
          {item.isPinned && (
            <StatusPill tone="warning" icon={<Pin size={12} />} size="sm" variant="outline">
              置頂
            </StatusPill>
          )}
          {isUnread && (
            <StatusPill tone="brand" dot size="sm" variant="outline">
              未讀
            </StatusPill>
          )}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: text.sm,
              color: color.text.muted,
              marginLeft: 'auto',
            }}
          >
            <Clock size={12} />
            {formatTime(item.publishedAt ?? item.createdAt)}
          </span>
        </div>

        {/* 標題 */}
        <h3
          onClick={() => onView(item)}
          style={{
            margin: 0,
            fontFamily: font.display,
            fontSize: text.lg,
            fontWeight: isUnread ? weight.bold : weight.semibold,
            color: color.text.primary,
            letterSpacing: tracking.tight,
            lineHeight: leading.tight,
            cursor: 'pointer',
            marginBottom: summary ? 6 : 0,
          }}
        >
          {item.title}
        </h3>

        {/* 摘要（兩行截斷） */}
        {summary && (
          <p
            className="line-clamp-2"
            style={{
              margin: 0,
              fontSize: text.md,
              color: color.text.tertiary,
              lineHeight: leading.snug,
            }}
          >
            {summary}
          </p>
        )}
      </div>

      {/* 右側操作 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: space[1],
          flexShrink: 0,
          paddingTop: 2,
        }}
      >
        <Tooltip title="檢視詳情">
          <Button type="text" size="small" icon={<Eye size={14} />} onClick={() => onView(item)} />
        </Tooltip>
        {isUnread && (
          <Tooltip title="標記已讀">
            <Button
              type="text"
              size="small"
              icon={<CheckCircle2 size={14} />}
              onClick={() => onMarkRead(item.id)}
              style={{ color: color.state.success }}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
 * 主頁面
 * ════════════════════════════════════════════════ */

function BulletinPage() {
  /* 當前用戶（用於前端按權限隱藏「新增」按鈕；後端仍會做權限校驗） */
  const user = useAuthStore((s) => s.user);
  const canPublish = user ? userCan(user, 'bulletin.publish') : false;

  const [items, setItems] = useState<Bulletin[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterKind, setFilterKind] = useState<FilterKind>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [current, setCurrent] = useState<Bulletin | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  /* ── 取資料 ── */
  const fetchBulletins = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.get<BulletinListResponse>('/api/bulletin');
      setItems(result.items);
    } catch {
      message.error('取得公告列表失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchBulletins();
  }, [fetchBulletins]);

  /* ── 客端搜尋 + 篩選（公告數量小，全量拉回後過濾足矣） ── */
  const filtered = useMemo(() => {
    return items.filter((b) => {
      if (filterKind === 'pinned' && !b.isPinned) return false;
      if (filterKind === 'unread' && b.isRead) return false;
      if (search) {
        const kw = search.toLowerCase();
        if (!b.title.toLowerCase().includes(kw) && !b.body.toLowerCase().includes(kw)) return false;
      }
      return true;
    });
  }, [items, search, filterKind]);

  const total = filtered.length;
  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );
  const unreadCount = useMemo(() => items.filter((n) => !n.isRead).length, [items]);

  /* ── 事件處理 ── */
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };
  const handleFilterChange = (value: FilterKind) => {
    setFilterKind(value);
    setPage(1);
  };
  const handleRefresh = () => void fetchBulletins();

  const handleOpenCreate = () => {
    form.resetFields();
    form.setFieldsValue({ audience: 'all', isPinned: false, publish: true });
    setModalOpen(true);
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await apiClient.post('/api/bulletin', values);
      message.success('公告發佈成功');
      setModalOpen(false);
      form.resetFields();
      setPage(1);
      void fetchBulletins();
    } catch {
      message.error('公告發佈失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      await apiClient.post(`/api/bulletin/${id}/read`, {});
      setItems((prev) =>
        prev.map((b) =>
          b.id === id ? { ...b, isRead: true, readAt: new Date().toISOString() } : b,
        ),
      );
    } catch {
      message.error('標記已讀失敗');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiClient.post(`/api/bulletin/read-all`, {});
      const now = new Date().toISOString();
      setItems((prev) => prev.map((b) => (b.isRead ? b : { ...b, isRead: true, readAt: now })));
      message.success('已將全部公告標記為已讀');
    } catch {
      message.error('操作失敗');
    }
  };

  const handleViewDetail = (b: Bulletin) => {
    setCurrent(b);
    setDetailOpen(true);
    if (!b.isRead) void handleMarkAsRead(b.id);
  };

  return (
    <PageShell
      title="系統公告"
      description="瀏覽機構內部公告與重要通知，掌握第一手訊息"
      actions={
        <>
          <Button icon={<RefreshCw size={14} />} onClick={handleRefresh}>
            重新整理
          </Button>
          {unreadCount > 0 && (
            <Button icon={<CheckCircle2 size={14} />} onClick={handleMarkAllRead}>
              全部已讀
            </Button>
          )}
          {canPublish && (
            <Button type="primary" icon={<Plus size={14} />} onClick={handleOpenCreate}>
              新增公告
            </Button>
          )}
        </>
      }
    >
      <SectionCard
        icon={<Megaphone size={14} />}
        title="公告列表"
        description={
          total > 0
            ? `共 ${total} 則公告${unreadCount > 0 ? `，其中 ${unreadCount} 則未讀` : ''}`
            : '暫無公告'
        }
        padless
      >
        {/* 篩選列 */}
        <div
          style={{
            padding: `${space[3]}px ${space[5]}px`,
            borderBottom: `1px solid ${color.border.subtle}`,
            background: color.surface.muted,
          }}
        >
          <FilterBar
            search={
              <Input.Search
                placeholder="搜尋公告標題或內容…"
                allowClear
                prefix={<Search size={14} style={{ color: color.text.muted }} />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onSearch={handleSearch}
              />
            }
            filters={
              <Select<FilterKind>
                value={filterKind}
                onChange={handleFilterChange}
                options={FILTER_OPTIONS}
                style={{ width: 160 }}
              />
            }
          />
        </div>

        {/* 列表內容 */}
        <Spin spinning={loading}>
          {pageItems.length === 0 && !loading ? (
            <EmptyState
              variant="empty"
              icon={<Bell size={40} strokeWidth={1.5} />}
              title="暫無公告"
              description={
                search || filterKind !== 'all'
                  ? '嘗試調整搜尋條件，或清除篩選查看全部'
                  : canPublish
                    ? '點擊「新增公告」發佈第一則公告'
                    : '目前尚無公告'
              }
              action={
                !search && filterKind === 'all' && canPublish ? (
                  <Button type="primary" icon={<Plus size={14} />} onClick={handleOpenCreate}>
                    新增公告
                  </Button>
                ) : null
              }
              size="lg"
            />
          ) : (
            <>
              {pageItems.map((item) => (
                <BulletinRow
                  key={item.id}
                  item={item}
                  onView={handleViewDetail}
                  onMarkRead={handleMarkAsRead}
                />
              ))}
              {total > PAGE_SIZE && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: `${space[5]}px 0`,
                    borderTop: `1px solid ${color.border.subtle}`,
                  }}
                >
                  <Pagination
                    current={page}
                    total={total}
                    pageSize={PAGE_SIZE}
                    onChange={setPage}
                    showSizeChanger={false}
                    showTotal={(t) => `共 ${t} 則公告`}
                  />
                </div>
              )}
            </>
          )}
        </Spin>
      </SectionCard>

      {/* 新增公告 Modal（僅 canPublish 才會打開） */}
      <Modal
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Megaphone size={16} style={{ color: color.brand.primary }} />
            新增公告
          </span>
        }
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleCreate}
        confirmLoading={submitting}
        okText="發佈"
        cancelText="取消"
        width={560}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: space[4] }}>
          <Form.Item
            name="title"
            label="公告標題"
            rules={[
              { required: true, message: '請輸入公告標題' },
              { max: 200, message: '標題最多 200 字' },
            ]}
          >
            <Input placeholder="請輸入公告標題" maxLength={200} showCount />
          </Form.Item>

          <Form.Item
            name="body"
            label="公告內容"
            rules={[{ required: true, message: '請輸入公告內容' }]}
          >
            <TextArea
              placeholder="支援 HTML（後續可升級為富文本編輯器）"
              rows={6}
              maxLength={2000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="audience"
            label="受眾"
            tooltip="目前所有已登入使用者均可看到所有公告，此欄位作為標籤資訊保留"
          >
            <Select
              options={[
                { value: 'all', label: '全體用戶' },
                { value: 'admin', label: '管理員' },
                { value: 'manager', label: '管理組' },
                { value: 'auditor', label: '稽核員' },
                { value: 'decider', label: '驗證決定組' },
              ]}
            />
          </Form.Item>

          <Form.Item name="isPinned" label="是否置頂" valuePropName="checked">
            <Switch checkedChildren="置頂" unCheckedChildren="一般" />
          </Form.Item>

          <Form.Item
            name="publish"
            label="立即發布"
            valuePropName="checked"
            tooltip="關閉時以草稿存檔，不會出現在公告列表頁；可至「公告管理」頁面再發布"
          >
            <Switch checkedChildren="立即" unCheckedChildren="存草稿" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 公告詳情 Modal */}
      <Modal
        title={
          current ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {getDisplayConfig(current).icon}
              <span style={{ color: color.text.primary }}>{current.title}</span>
            </span>
          ) : (
            '公告詳情'
          )
        }
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setCurrent(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setDetailOpen(false);
              setCurrent(null);
            }}
          >
            關閉
          </Button>,
        ]}
        width={640}
      >
        {current && (
          <div style={{ paddingBlock: space[4] }}>
            {/* 標籤行 */}
            <div
              style={{
                display: 'flex',
                gap: space[2],
                marginBottom: space[4],
                alignItems: 'center',
              }}
            >
              <StatusPill
                tone={getDisplayConfig(current).tone}
                icon={getDisplayConfig(current).icon}
              >
                {getDisplayConfig(current).label}
              </StatusPill>
              {current.isPinned && (
                <StatusPill tone="warning" icon={<Pin size={12} />} size="sm">
                  置頂
                </StatusPill>
              )}
              <StatusPill tone={current.isRead ? 'muted' : 'brand'} dot size="sm">
                {current.isRead ? '已讀' : '未讀'}
              </StatusPill>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: text.sm,
                  color: color.text.tertiary,
                  marginLeft: 'auto',
                }}
              >
                <Clock size={12} />
                {dayjs(current.publishedAt ?? current.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </span>
            </div>

            {/* 內容（純文字，pre-wrap 保留段落） */}
            <div
              style={{
                background: color.surface.muted,
                borderRadius: radius.md,
                padding: `${space[4]}px ${space[5]}px`,
                minHeight: 120,
                fontSize: text.md,
                color: color.text.primary,
                lineHeight: leading.relaxed,
                border: `1px solid ${color.border.subtle}`,
                /* 序94：公告以純文字 TextArea 撰寫，pre-wrap 保留段落與空行分段；
                   純文字直接渲染（非 HTML），天然防 XSS */
                whiteSpace: 'pre-wrap',
              }}
            >
              {current.body}
            </div>
          </div>
        )}
      </Modal>
    </PageShell>
  );
}
